import { useEffect } from "react";
import type { Phase } from "../domain/enums";
import { phaseName } from "../domain/i18n";
import type { Language } from "../domain/enums";
import { phaseFlash as hapticPhaseFlash } from "../lib/haptic";

const PHASE_GLYPH: Record<Phase, string> = {
  warmup: "❋",
  engagement: "✦",
  peak: "✺",
};

interface Props {
  phase: Phase;
  language: Language;
  onDone: () => void;
  /** ms */
  duration?: number;
}

/**
 * Full-screen neon flash for phase transitions (and the first card of a
 * session). Designed to read at arm's length: a giant glyph + phase name
 * with radial flare and a pulse ring.
 */
export function PhaseFlash({ phase, language, onDone, duration = 1500 }: Props) {
  useEffect(() => {
    // Fire haptic on entrance — host feels phase shift even if their gaze is
    // on the group rather than the screen.
    hapticPhaseFlash();
    const id = setTimeout(onDone, duration);
    return () => clearTimeout(id);
  }, [onDone, duration]);

  return (
    <div
      className="absolute inset-0 z-30 flex flex-col items-center justify-center backdrop-blur-md overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse at center, color-mix(in srgb, var(--accent, #ff4ed8) 30%, transparent) 0%, rgba(0,0,0,0.85) 70%)",
        animation: "pp-phase-fade 1500ms ease-out",
      }}
    >
      <div
        className="absolute"
        style={{
          width: 340,
          height: 340,
          borderRadius: "50%",
          border: "2px solid var(--accent, #ff4ed8)",
          opacity: 0.45,
          animation: "pp-phase-ring 1300ms ease-out",
        }}
      />
      <div
        className="absolute"
        style={{
          width: 220,
          height: 220,
          borderRadius: "50%",
          border: "2px solid var(--accent, #ff4ed8)",
          opacity: 0.6,
          animation: "pp-phase-ring 1100ms ease-out 100ms backwards",
        }}
      />
      <div
        className="relative text-9xl font-black"
        style={{
          color: "var(--accent, #ff4ed8)",
          textShadow:
            "0 0 24px var(--accent, #ff4ed8), 0 0 48px var(--accent, #ff4ed8)",
          animation: "pp-phase-pop 700ms cubic-bezier(.2,.9,.3,1.2)",
        }}
      >
        {PHASE_GLYPH[phase]}
      </div>
      <div
        className="relative font-display text-5xl font-extrabold tracking-wide mt-3 uppercase"
        style={{
          color: "#fff",
          textShadow: "0 0 18px var(--accent, #ff4ed8)",
          animation: "pp-phase-pop 700ms cubic-bezier(.2,.9,.3,1.2) 80ms backwards",
        }}
      >
        {phaseName(language, phase)}
      </div>
    </div>
  );
}
